import { useDataContext } from 'context/DataStoreContext';
import { useApi } from 'model/Api';

/**
 * 全権限定義リスト。
 * フロント・バックエンド両方の実装から参照されている権限キーをここで一元管理する。
 * - DBには detail.permissions JSON にスネークケースで保存される
 * - APIレスポンス→フロントの toModel 変換でキャメルケースに変換され、ここの code と一致する
 *
 * label は仮テキスト。UI 文言調整は別タスクで行う想定。
 */
export const permissions = [
  { code: 'root',             label: 'システム管理者' },
  { code: 'login',            label: 'ログイン' },
  { code: 'workManage',       label: '安全責任者（上長）' },
  { code: 'workSupervise',    label: '現場代理人' },
  { code: 'workAssign',       label: '作業従事' },
  { code: 'ledgerEdit',       label: '工事台帳の編集' },
  { code: 'ledgerShow',       label: '工事台帳の閲覧' },
  { code: 'reportEdit',       label: '日報の編集' },
  { code: 'reportCreate',     label: '日報の作成' },
  { code: 'userEdit',         label: 'メンバー・協力業者の編集' },
  { code: 'masterDataManage', label: '工具・重機の管理' },
] as const;

export type Permission = typeof permissions[number]['code'];

export type Role = {
  id: number;
  code: string;
  name: string;
  permissions: { [permission in Permission]: boolean };
  /** 個別ユーザー用のカスタム役職か否か */
  isCustom?: boolean;
  /** カスタム役職の元になった役職のID */
  parentId?: number | null;
};

/** 全 Permission キーを false で初期化した permissions オブジェクトを返す */
export const createEmptyPermissions = (): Role['permissions'] =>
  permissions.reduce(
    (acc, { code }) => ({ ...acc, [code]: false }),
    {} as Role['permissions'],
  );

/**
 * APIから受け取った（あるいは未定義キーがありうる）permissions を、
 * 全 Permission キーを必ず持つ厳密な形に正規化する。
 */
export const normalizePermissions = (
  raw?: Partial<Record<string, unknown>>,
): Role['permissions'] =>
  permissions.reduce(
    (acc, { code }) => ({ ...acc, [code]: !!raw?.[code] }),
    {} as Role['permissions'],
  );

/** APIから受け取った Role を、permissions を正規化した形に整える */
export const normalizeRole = <T extends { permissions?: Partial<Record<string, unknown>> }>(
  raw: T,
): T & { permissions: Role['permissions'] } => ({
  ...raw,
  permissions: normalizePermissions(raw.permissions),
});

export const useRole = () => {
  const api = useApi();
  const { state, dispatch } = useDataContext();

  const getList = (): Role[] => {
    const roles = state?.role.list || [];
    if (!roles.length) {
      api.role.getsByOwn().then((rolesResponse) => {
        dispatch?.({ type: 'SET_ROLE_LIST', payload: rolesResponse });
      });
    }

    return roles;
  };

  const getListAsync = async (): Promise<Role[]> => {
    const stateRoles = state?.role.list || [];
    const roles = stateRoles.length
      ? stateRoles
      : (await api.role.getsByOwn()) || [];
    if (!stateRoles.length) {
      dispatch?.({ type: 'SET_ROLE_LIST', payload: roles });
    }

    return roles;
  };

  return { getList, getListAsync };
};
